import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../stores/auth.store';
import { durationFromNow, fmtTime } from '../lib/format';
import { BottomNav } from '../components/BottomNav';

interface ActiveDuty {
  id: string;
  locationName: string;
  startAt: string;
  dutyType: { name: string };
}

export function HomePage() {
  const officer = useAuth((s) => s.officer);
  const [active, setActive] = useState<ActiveDuty | null>(null);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(Date.now());
  const [pendingCount, setPendingCount] = useState(0);
  const navigate = useNavigate();
  const isSupervisor = officer && officer.role !== 'officer';

  async function refresh() {
    try {
      const { data } = await api.get<ActiveDuty | null>('/app/duty-logs/active');
      setActive(data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  useEffect(() => {
    if (!isSupervisor) return;
    api
      .get<{ total: number }>('/app/sv/officers/pending')
      .then((r) => setPendingCount(r.data.total))
      .catch(() => undefined);
  }, [isSupervisor]);

  useEffect(() => {
    if (!active) return;
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, [active]);

  void now;

  return (
    <div className="mx-auto min-h-screen max-w-md bg-gray-50 pb-24">
      <header className="bg-line px-4 pb-6 pt-8 text-white">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-sm opacity-90">สวัสดี</p>
            <h1 className="text-xl font-bold">
              {officer?.firstName} {officer?.lastName}
            </h1>
            <p className="mt-1 text-xs opacity-80">เจ้าหน้าที่ อปพร. เทศบาลนครนครปฐม</p>
          </div>
          {isSupervisor && (
            <span className="rounded-full bg-white/20 px-2 py-0.5 text-xs font-medium backdrop-blur">
              🛡 {officer?.role === 'admin' ? 'Admin' : 'ผู้ดูแล'}
            </span>
          )}
        </div>
      </header>

      <main className="space-y-4 p-4">
        {loading ? (
          <div className="card text-center text-sm text-gray-500">กำลังโหลด...</div>
        ) : active ? (
          <div className="card border-l-4 border-line">
            <p className="text-xs text-gray-500">กำลังปฏิบัติงาน</p>
            <h2 className="mt-1 text-lg font-bold">{active.locationName}</h2>
            <p className="text-sm text-gray-600">{active.dutyType.name}</p>
            <div className="mt-3 flex items-center justify-between text-sm">
              <span className="text-gray-500">
                เริ่ม {fmtTime(active.startAt)} • ผ่านไป {durationFromNow(active.startAt)}
              </span>
            </div>
            <button
              onClick={() => navigate('/duty/active')}
              className="btn-primary mt-4"
            >
              ดู / จบงาน
            </button>
          </div>
        ) : (
          <div className="card text-center">
            <div className="text-5xl">🚓</div>
            <p className="mt-3 text-base font-medium">ยังไม่มีภารกิจที่กำลังทำ</p>
            <p className="mt-1 text-sm text-gray-500">กดปุ่มด้านล่างเพื่อเริ่มบันทึก</p>
            <Link to="/duty/start" className="btn-primary mt-4">
              + เริ่มบันทึกการปฏิบัติงาน
            </Link>
          </div>
        )}

        <Link to="/history" className="card flex items-center justify-between">
          <div>
            <p className="font-medium">ประวัติการปฏิบัติงาน</p>
            <p className="text-xs text-gray-500">ดูบันทึกย้อนหลังของฉัน</p>
          </div>
          <span className="text-2xl text-gray-400">›</span>
        </Link>

        {isSupervisor && (
          <section className="space-y-2 pt-2">
            <p className="px-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
              ส่วนผู้ดูแล
            </p>
            <Link to="/supervisor/duty-logs" className="card flex items-center gap-3">
              <span className="text-2xl">👥</span>
              <div className="flex-1">
                <p className="font-medium">บันทึกทั้งหมด</p>
                <p className="text-xs text-gray-500">ดูบันทึกของเจ้าหน้าที่ทุกคน</p>
              </div>
              <span className="text-2xl text-gray-400">›</span>
            </Link>
            <Link to="/supervisor/officers" className="card flex items-center gap-3">
              <span className="text-2xl">✅</span>
              <div className="flex-1">
                <p className="font-medium">อนุมัติเจ้าหน้าที่</p>
                <p className="text-xs text-gray-500">
                  {pendingCount > 0 ? `รออนุมัติ ${pendingCount} คน` : 'ไม่มีรายการรออนุมัติ'}
                </p>
              </div>
              {pendingCount > 0 && (
                <span className="rounded-full bg-red-500 px-2 py-0.5 text-xs font-medium text-white">
                  {pendingCount}
                </span>
              )}
              <span className="text-2xl text-gray-400">›</span>
            </Link>
            <Link to="/supervisor/dashboard" className="card flex items-center gap-3">
              <span className="text-2xl">📊</span>
              <div className="flex-1">
                <p className="font-medium">สรุปวันนี้</p>
                <p className="text-xs text-gray-500">ภาพรวมการปฏิบัติงานในวันนี้</p>
              </div>
              <span className="text-2xl text-gray-400">›</span>
            </Link>
          </section>
        )}
      </main>

      <BottomNav />
    </div>
  );
}
